import type { PtFile } from '@/api/types/pt_entities'
import type { FileType } from '@/api/types/pt_enums'

export interface ResponseModel<T> {
  code?: number
  message?: string
  data?: T
}

/**
 * 消息
 * 群聊: group_${groupId}，私聊: private_${minUserId}_${maxUserId}
 */
export interface WsMessage {
  /** 消息Id(请求与响应的消息Id一致) */
  msgId?: string
  /** 类型: 对应WS_MSG_TYPE */
  type?: string
  /** 消息内容 */
  data?: string | Record<string, any>
}

export enum WS_MSG_TYPE {
  PING = 'ping',
  PONG = 'pong',
  COMMAND = 'command',
  ERROR = 'error',
  SUCCESS = 'success'
}

export class PtError extends Error {
  code: string | number
  message: string
  constructor(code: string | number, message: string) {
    super(`${code} - ${message}`) // 调用父类构造函数，初始化 message
    this.name = 'PtError' // 自定义错误名称
    this.code = code // 添加自定义 code 属性
    this.message = message // 可以覆盖或保留父类的 message
    // this.stack = new Error(message).stack // 确保堆栈跟踪正确
  }
}

export type EnumValue = string | number
export interface EnumItem<V extends EnumValue = EnumValue> {
  readonly label: string
  readonly value: V
  toString(): string
  toJSON(): V
}
export interface EnumType<T extends EnumItem<V>, V extends EnumValue = EnumValue> {
  values(): readonly T[]
  fromValue(value?: V): T | undefined
}

export interface CheckPropertyType {
  prop: string
  value?: string | number | boolean
  checkOp: string
}

export class FileInfo {
  fileName: string
  filePath: string
  fileSize: number
  contentType?: string
  fileContent?: Blob

  constructor(fileName: string, filePath: string, fileSize?: number) {
    this.fileName = fileName
    this.filePath = filePath
    this.fileSize = fileSize || 0
  }
}

export interface DeviceInfoType {
  width?: number
  height?: number
  isLandscape?: boolean
  isTouch?: boolean
  isMobile?: boolean
}

export type CSSOrClassType = string | string[] | Record<string, any>
export interface ActionType {
  type?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | ''
  text?: string
  icon?: string
  title?: string
  disabled?: boolean | ((data?: any) => boolean)
  visible?: boolean | ((data?: any) => boolean)
  plain?: boolean
  privilege?: string
  action?: (data?: any) => void
  children?: ActionType[]
  childWidth?: number
  childPlacement?: 'top' | 'bottom' | 'left' | 'right'
  childDirection?: 'horizontal' | 'vertical'
  childGap?: string | number
}

export type IconType = 'icon' | 'image'
export interface CustomTabBarItem {
  text: string
  path?: string
  icon: string
  iconActive?: string
  iconType: IconType
  badge?: number | 'dot'
  isBulge?: boolean
}

export interface ActionSheetOptions {
  items: string[] | number[] | object[]
  labelProp?: string
  success: (item: string | number | object) => void
}

export interface RegionType {
  id: number
  parentId: number
  name: string
  shortName: string
  fullName: string
  level: number
  pyinyin: string
  path: number[]
  children: number[]
}

export interface CategoryType {
  id: number
  parentId?: number
  name?: string
  type?: string
  level: number
  children?: number[]
  fullName?: string
  path?: number[]
}

export interface UploadTask {
  /** 启动上传 */
  start(): Promise<PtFile>

  /** 取消上传 */
  cancel(): void

  /** 当前进度 0~100 */
  progress: number
}

export interface UploadOptions {
  type?: FileType

  onProgress?: (percent: number, loaded: number, total: number) => void

  /** 大文件 */
  // chunked?: boolean
  // chunkSize?: number
}

export interface Uploader {
  createTask(file: FileInfo, options?: UploadOptions): UploadTask
}

export interface RequestOptions {
  silent?: boolean // 不自动 toast
  redirectToLogin?: boolean // NOT_LOGIN时是否要跳转到登录页
}

export type CronItemValueType = 'one' | 'some' | 'range' | 'step' | 'every' | 'last' | 'no' | 'work' | 'any'
export type CronItemType = 'second' | 'minute' | 'hour' | 'date' | 'month' | 'week'
export const cronUnitMap: Record<CronItemType, string> = {
  second: '秒钟',
  minute: '分钟',
  hour: '小时',
  date: '天',
  month: '月',
  week: '天'
}
export const cronTabs = [
  { label: '秒钟', value: 'second' },
  { label: '分钟', value: 'minute' },
  { label: '小时', value: 'hour' },
  { label: '日期', value: 'date' },
  { label: '月份', value: 'month' },
  { label: '星期', value: 'week' }
]

export type JobDataMap = Record<string, any>
export type JsonObject = Record<string, any>
export type JsonArray = Array<any>
export interface GeoPoint {
  lng: number
  lat: number
}

export interface ProfitConfigType {
  targetId?: number
  targetType?: string
  targetName?: string
  INCOME_1?: string
  INCOME_2?: string
  INCOME_3?: string
  SERVICE_FEE_1?: string
  SERVICE_FEE_2?: string
  SERVICE_FEE_3?: string
  CHANNEL_FEE_1?: string
}
